// File: src/pages/reveal/RevealQuiz.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { LuArrowLeft, LuCheck } from "react-icons/lu";
import EventHelpTooltip from "../../components/EventPreview/EventHelpTooltip";
import { ReactComponent as TribeLogo } from "../../assets/TribeLogo.svg";
import UnsavedChangesBottomSheet from "../../components/UnsavedChangesBottomSheet";

export default function RevealQuiz() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const api = process.env.REACT_APP_API_URL;

  /* auth helpers */
  const token = useMemo(
    () =>
      localStorage.getItem("accessToken") ||
      JSON.parse(localStorage.getItem("tribe_auth") || "{}")?.token ||
      "",
    []
  );
  const authHeaders = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    [token]
  );
  const getUserId = () => {
    const ctx = JSON.parse(localStorage.getItem("tribe_auth") || "{}");
    return (
      ctx?.uid ||
      ctx?.userId ||
      ctx?.user?._id ||
      localStorage.getItem("userId") ||
      ""
    );
  };

  /* state */
  const [loading, setLoading] = useState(true);
  const [qs, setQs] = useState([]);           // [{ key, type, text, options?, min?, max? }]
  const [answers, setAnswers] = useState({}); // { [key]: string | string[] | number }
  const [idx, setIdx] = useState(0);
  const [error, setError] = useState("");
  const [showUnsavedSheet, setShowUnsavedSheet] = useState(false);

  const currentQ = qs[idx] || {};
  const answeredCurrent = useMemo(() => {
    const v = answers[currentQ?.key];
    if (currentQ?.type === "slider") return typeof v === "number";
    if (currentQ?.type === "single") return typeof v === "string" && !!v;
    if (currentQ?.type === "multi") return Array.isArray(v) && v.length > 0;
    return false;
  }, [answers, currentQ]);

  /* load questions + access (explicit gate) */
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");

      // Access check – branch on denial so the correct sheet shows on the Event page
      try {
        const res = await axios.get(`${api}/reveal-mode/${eventId}/access`, {
          ...authHeaders,
          validateStatus: (s) => s === 200 || s === 401 || s === 403,
          headers: {
            ...(authHeaders.headers || {}),
            "X-Suppress-Global-Error": "1",
          },
        });

        if (!(res.status === 200 && res.data?.ok)) {
          const reason = res?.data?.reason || "";
          if (reason === "not_registered") {
            navigate(`/events/reveal-${eventId}`, { replace: true, state: { showRegSheet: true } });
            return;
          }
          if (reason === "out_of_fence" || reason === "no_location") {
            navigate(`/events/reveal-${eventId}`, { replace: true, state: { showGeoSheet: true } });
            return;
          }
          if (reason === "gender_gate") {
            navigate(`/events/reveal-${eventId}`, { replace: true, state: { showOrientationDenied: true } });
            return;
          }
          if (reason === "too_early") {
            navigate(`/events/reveal-${eventId}`, { replace: true });
            return;
          }
          // If reason === quiz_required/quiz_incomplete → that’s fine; we’re on the quiz.
        }
      } catch {
        // Network/unknown → conservative bounce to event card
        navigate(`/events/reveal-${eventId}`, { replace: true });
        return;
      }

      // Load questions
      try {
        const { data } = await axios.get(`${api}/reveal-mode/${eventId}`);
        const list = Array.isArray(data?.questions) ? data.questions : [];
        if (!alive) return;

        const init = {};
        for (const q of list) {
          if (q.type === "slider") init[q.key] = Number.isFinite(q.min) ? q.min : 0;
          if (q.type === "multi") init[q.key] = [];
          if (q.type === "single") init[q.key] = "";
        }

        setQs(list);
        setAnswers((prev) => ({ ...init, ...prev }));
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.message || "Failed to load quiz.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, eventId, authHeaders, navigate]);

  /* setters */
  const setSingle = (key, value) => setAnswers((p) => ({ ...p, [key]: value }));
  const toggleMulti = (key, value) =>
    setAnswers((p) => {
      const cur = Array.isArray(p[key]) ? p[key] : [];
      return cur.includes(value)
        ? { ...p, [key]: cur.filter((v) => v !== value) }
        : { ...p, [key]: [...cur, value] };
    });
  const setSlider = (key, value) => setAnswers((p) => ({ ...p, [key]: Number(value) }));

  /* nav */
  const handleTopBackClick = () => setShowUnsavedSheet(true);
  const handleBack = () => {
    if (idx > 0) setIdx((i) => i - 1);
  };
  const handleNext = () => {
    if (answeredCurrent) setIdx((i) => Math.min(qs.length - 1, i + 1));
  };

  /* submit */
  const submit = async () => {
    try {
      setLoading(true);
      setError("");

      // ensure all answered
      const missing = qs.filter((q) => {
        const v = answers[q.key];
        if (q.type === "slider") return typeof v !== "number";
        if (q.type === "single") return typeof v !== "string" || !v;
        if (q.type === "multi") return !Array.isArray(v) || v.length === 0;
        return true;
      });
      if (missing.length) {
        setError("Please answer all questions.");
        setLoading(false);
        return;
      }

      const userId = getUserId();
      if (!userId && !authHeaders.headers?.Authorization) {
        setError("You must be signed in to submit.");
        setLoading(false);
        return;
      }

      await axios.post(`${api}/reveal-mode/${eventId}/submit`, { userId, answers }, authHeaders);
      navigate(`/reveal/${eventId}/portal`, { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Submit failed";
      console.error("[RevealQuiz submit] error:", msg, e?.response?.data);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const stepText = useMemo(
    () => `Question ${Math.min(idx + 1, qs.length)} of ${qs.length || 0}`,
    [idx, qs.length]
  );

  /* render */
  if (loading && qs.length === 0)
    return <div style={{ padding: 20, fontFamily: "Inter, system-ui" }}>Loading quiz…</div>;
  if (error)
    return (
      <div style={{ padding: 20, color: "#b91c1c", fontFamily: "Inter, system-ui" }}>
        {error}
      </div>
    );
  if (!qs.length)
    return <div style={{ padding: 20, fontFamily: "Inter, system-ui" }}>No quiz found.</div>;

  /* slider values */
  const min = Number.isFinite(currentQ.min) ? currentQ.min : 0;
  const max = Number.isFinite(currentQ.max) ? currentQ.max : 100;
  const cur = typeof answers[currentQ.key] === "number" ? answers[currentQ.key] : min;
  const span = Math.max(1, max - min);
  const pct = Math.max(0, Math.min(100, ((cur - min) / span) * 100));

  // compute slider thumb position
  const thumbLeft =
    pct >= 100
      ? `calc(100% - ${S.THUMB}px)`
      : pct <= 0
      ? `0px`
      : `calc(${pct}% - ${S.THUMB / 2}px)`;

  return (
    <div style={S.app}>
      {/* Header */}
      <header style={S.header}>
        <button style={S.backBtn} onClick={handleTopBackClick} aria-label="Go back">
          <LuArrowLeft size={16} />
        </button>
        <div style={S.logoWrap} aria-hidden="true">
          <TribeLogo style={S.logoSvg} />
        </div>
        <div style={S.rightWrap}>
          <EventHelpTooltip
            title="About the Quiz"
            body="Answer each prompt honestly. Your responses help us pair you well. You can’t skip a question."
          />
        </div>
      </header>

      {/* Page */}
      <div style={S.page}>
        <div style={S.stepText}>{stepText}</div>

        <h1 style={S.questionText}>{currentQ?.text || "—"}</h1>

        {/* hint for MULTI */}
        {currentQ?.type === "multi" && (
          <div style={S.hintText}>You can select more than one option.</div>
        )}

        <div style={S.optionsWrap}>
          {/* SINGLE */}
          {currentQ?.type === "single" &&
            (currentQ.options || []).map((opt) => {
              const checked = answers[currentQ.key] === opt.value;
              return (
                <label
                  key={opt.value}
                  htmlFor={`q-${currentQ.key}-${opt.value}`}
                  style={{ ...S.optionItem, ...(checked ? S.optionItemActive : {}) }}
                >
                  <input
                    id={`q-${currentQ.key}-${opt.value}`}
                    type="radio"
                    name={`q-${currentQ.key}`}
                    value={opt.value}
                    checked={checked}
                    onChange={() => setSingle(currentQ.key, opt.value)}
                    style={S.radioInput}
                  />
                  <span style={S.optionLabel}>{opt.label}</span>
                  <span
                    aria-hidden
                    style={{ ...S.checkBubble, ...(checked ? S.checkBubbleActive : {}) }}
                  >
                    {checked && <LuCheck size={12} color="#fff" />}
                  </span>
                </label>
              );
            })}

          {/* MULTI */}
          {currentQ?.type === "multi" &&
            (currentQ.options || []).map((opt) => {
              const curVals = Array.isArray(answers[currentQ.key])
                ? answers[currentQ.key]
                : [];
              const checked = curVals.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  htmlFor={`q-${currentQ.key}-${opt.value}`}
                  style={{ ...S.optionItem, ...(checked ? S.optionItemActive : {}) }}
                >
                  <input
                    id={`q-${currentQ.key}-${opt.value}`}
                    type="checkbox"
                    value={opt.value}
                    checked={checked}
                    onChange={() => toggleMulti(currentQ.key, opt.value)}
                    style={S.radioInput}
                  />
                  <span style={S.optionLabel}>{opt.label}</span>
                  <span
                    aria-hidden
                    style={{ ...S.checkBubble, ...(checked ? S.checkBubbleActive : {}) }}
                  >
                    {checked && <LuCheck size={12} color="#fff" />}
                  </span>
                </label>
              );
            })}

          {/* SLIDER — larger, centered, precise thumb alignment */}
          {currentQ?.type === "slider" && (
            <div style={S.sliderWrap}>
              <div style={S.sliderValueWrap}>
                <span style={S.sliderValue}>{cur}</span>
              </div>

              <div style={S.sliderTrack}>
                {/* fill */}
                <div
                  style={{
                    ...S.sliderFill,
                    width: `${pct}%`,
                    borderTopRightRadius: pct === 100 ? 999 : 0,
                    borderBottomRightRadius: pct === 100 ? 999 : 0,
                  }}
                />
                {/* thumb */}
                <div
                  style={{
                    ...S.sliderThumb,
                    left: thumbLeft,
                  }}
                />
                <input
                  type="range"
                  min={min}
                  max={max}
                  step={1}
                  value={cur}
                  onChange={(e) => setSlider(currentQ.key, e.target.value)}
                  style={S.sliderInputOverlay}
                  aria-valuemin={min}
                  aria-valuemax={max}
                  aria-valuenow={cur}
                />
              </div>

              <div style={S.sliderScale}>
                <span>{min}</span>
                <span>{max}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom nav */}
      <div style={S.buttonBar} role="group" aria-label="Quiz navigation">
        <div style={S.buttonRow}>
          <button
            onClick={handleBack}
            style={{ ...S.backBtnBottom, ...(idx === 0 ? S.backBtnDisabled : {}) }}
            disabled={idx === 0}
          >
            Back
          </button>

          {idx < qs.length - 1 ? (
            <button
              onClick={handleNext}
              style={{ ...S.nextBtn, ...(answeredCurrent ? {} : S.nextBtnDisabled) }}
              disabled={!answeredCurrent}
            >
              Next
            </button>
          ) : (
            <button
              onClick={submit}
              style={{ ...S.nextBtn, ...(answeredCurrent ? {} : S.nextBtnDisabled) }}
              disabled={!answeredCurrent || loading}
            >
              {loading ? "Submitting…" : "Submit"}
            </button>
          )}
        </div>
      </div>

      {/* Confirm-exit */}
      {showUnsavedSheet && (
        <UnsavedChangesBottomSheet
          onCancel={() => setShowUnsavedSheet(false)}
          onDiscard={() => {
            setShowUnsavedSheet(false);
            if (state?.event) navigate(`/reveal/${eventId}/intro`, { state });
            else navigate(`/reveal/${eventId}/intro`);
          }}
        />
      )}
    </div>
  );
}

/* -------------------- Styles -------------------- */
const COLOR = {
  ink: "#0B1020",
  sub: "#3A4050",
  line: "#E7E9EE",
  headerBg: "#FFFFFF",
  chip: "#F3F4F6",
  chipBorder: "#E5E7EB",
  black: "#0b0b0b",
  primary: "#3579F6",
};

const GRIDW = "min(720px, 92vw)";

const S = {
  app: {
    fontFamily:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    color: COLOR.ink,
    background: "#fff",
  },

  /* Header */
  header: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    width: "100%",
    maxWidth: 1200,
    margin: "0 auto",
    height: 56,
    display: "grid",
    alignItems: "center",
    gridTemplateColumns: "1fr auto 1fr",
    background: COLOR.headerBg,
  },
  backBtn: {
    justifySelf: "start",
    marginLeft: 12,
    background: COLOR.black,
    color: "#fff",
    border: "none",
    borderRadius: 9999,
    width: 40,
    height: 26,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  logoWrap: {
    justifySelf: "center",
    display: "flex",
    alignItems: "center",
    pointerEvents: "none",
  },
  logoSvg: { width: 72, height: "auto", display: "block" },
  rightWrap: {
    justifySelf: "end",
    marginRight: 12,
    display: "flex",
    alignItems: "center",
    height: 32,
  },

  /* Page */
  page: { width: GRIDW, margin: "14px auto 120px", padding: 0 },

  /* Step pill */
  stepText: {
    display: "inline-block",
    background: COLOR.chip,
    border: `1px solid ${COLOR.chipBorder}`,
    color: COLOR.ink,
    borderRadius: 999,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1,
    margin: "6px 0 10px",
  },

  /* Question */
  questionText: {
    margin: "6px 0 6px",
    fontSize: "clamp(1.35rem, 3.2vw, 1.9rem)",
    fontWeight: 900,
    letterSpacing: "-0.01em",
    lineHeight: 1.2,
    textAlign: "left",
  },

  /* Guidance hint for multi */
  hintText: {
    marginTop: 4,
    marginBottom: 8,
    fontSize: 12.5,
    color: "#6b7280",
    fontWeight: 700,
  },

  /* Options */
  optionsWrap: { display: "grid", gap: 10, marginTop: 10 },
  optionItem: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 10,
    border: `1px solid ${COLOR.line}`,
    background: "#fff",
    boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
    cursor: "pointer",
    transition: "border-color 120ms ease, background 120ms ease",
  },
  optionItemActive: { border: `1px solid ${COLOR.black}`, background: "#F7FAFF" },
  radioInput: { position: "absolute", opacity: 0, pointerEvents: "none" },
  optionLabel: { flex: 1, fontSize: 14.5, color: COLOR.ink },
  checkBubble: {
    width: 20,
    height: 20,
    borderRadius: 999,
    border: `2px solid ${COLOR.line}`,
    background: "#fff",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  checkBubbleActive: { background: COLOR.black, border: `2px solid ${COLOR.black}` },

  /* Slider constants */
  THUMB: 20,

  /* Custom Slider (large, centered, blue) */
  sliderWrap: {
    marginTop: 150,
    marginBottom: 6,
    display: "grid",
    gap: 12,
    justifyItems: "center",
  },
  sliderValueWrap: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 14px",
    borderRadius: 12,
    background: "#eee",
    border: "0px solid #eee",
    boxShadow: "0 1px 2px rgba(30,64,175,0.06)",
    marginTop: 4,
    marginBottom: 6,
  },
  sliderValue: {
    fontSize: 38,
    fontWeight: 900,
    letterSpacing: "0.01em",
    color: COLOR.ink,
    lineHeight: 1,
    margin: 0,
  },
  sliderTrack: {
    position: "relative",
    width: "min(680px, 92vw)",
    height: 20,
    background: "#fff",
    border: "1px solid #E7E9EE",
    borderRadius: 999,
    overflow: "hidden",
  },
  sliderFill: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    background: "#D6E8FF",
    borderRadius: 999,
  },

  sliderThumb: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 20,
    height: 20,
    borderRadius: 999,
    background: COLOR.primary,
    border: "0px solid " + COLOR.primary,
    boxShadow: "0 1px 2px rgba(16,24,40,0.12)",
    pointerEvents: "none",
    zIndex: 1,
  },
  sliderInputOverlay: {
    position: "absolute",
    inset: -8,
    width: "calc(100% + 16px)",
    height: "calc(100% + 16px)",
    opacity: 0,
    cursor: "pointer",
  },
  sliderScale: {
    width: "min(680px, 92vw)",
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12,
    color: "#6b7280",
    fontWeight: 700,
  },

  /* Bottom buttons */
  buttonBar: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    padding: "12px 20px calc(12px + env(safe-area-inset-bottom))",
    background: "transparent",
    pointerEvents: "none",
  },
  buttonRow: {
    pointerEvents: "auto",
    margin: "0 auto",
    width: "100%",
    display: "flex",
    gap: 10,
  },
  backBtnBottom: {
    flex: "0 0 32%",
    height: 44,
    borderRadius: 10,
    background: "#FFFFFF",
    color: COLOR.ink,
    border: `1px solid ${COLOR.line}`,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  backBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  nextBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    border: `1px solid ${COLOR.black}`,
    background: COLOR.black,
    color: "#fff",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  nextBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
};